import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Play, Pause, Plus, Trash2, X } from "lucide-react";
import { Tile } from "@/components/common/Tile";
import { Loading } from "@/components/common/Loading";
import { Pathogen } from "@/components/common/Pathogen";
import { useToast } from "@/components/common/Toast";
import {
  useAlerts, useCreateAlert, useDeleteAlert, useUpdateAlert,
} from "@/hooks/useAlerts";
import { qpcrApi } from "@/api/qpcr";
import type {
  AlertRule, AlertRuleCreate, Comparator, Metric,
} from "@/api/alerts";

const COMPARATORS: { value: Comparator; label: string }[] = [
  { value: "gt", label: ">" },
  { value: "gte", label: "≥" },
  { value: "lt", label: "<" },
  { value: "lte", label: "≤" },
];

const METRIC_LABELS: Record<Metric, string> = {
  concentration: "concentration (copies/mL)",
  trend_pct: "trend Δ (%)",
};

function describe(r: AlertRule): string {
  const op = COMPARATORS.find(c => c.value === r.comparator)?.label ?? r.comparator;
  const metric = METRIC_LABELS[r.metric];
  const thresh = r.metric === "trend_pct"
    ? `${r.threshold}%`
    : `${r.threshold.toLocaleString()} copies/mL`;
  return `If ${metric} is ${op} ${thresh}`;
}

function NewAlertModal({ deviceId, onClose }: { deviceId: string; onClose: () => void }) {
  const { data: pathData } = useQuery({
    queryKey: ["pathogens"],
    queryFn: () => qpcrApi.pathogens(),
    staleTime: 60_000,
  });
  const create = useCreateAlert(deviceId);
  const toast = useToast();

  const [pathogenId, setPathogenId] = useState<string>("");
  const [metric, setMetric] = useState<Metric>("concentration");
  const [comparator, setComparator] = useState<Comparator>("gt");
  const [threshold, setThreshold] = useState<string>("10000");

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const body: AlertRuleCreate = {
      pathogen_id: pathogenId ? Number(pathogenId) : null,
      metric,
      comparator,
      threshold: Number(threshold),
    };
    if (!Number.isFinite(body.threshold)) return;
    create.mutate(body, {
      onSuccess: () => {
        toast.push("Alert created", "success");
        onClose();
      },
      onError: () => toast.push("Failed to create alert", "error"),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-md" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="relative z-10 w-full max-w-md rounded-lg bg-surface shadow-card p-lg flex flex-col gap-md"
      >
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-text">New alert</h2>
          <button type="button" onClick={onClose} aria-label="Close" className="text-textLight hover:text-text cursor-pointer">
            <X className="w-4 h-4" />
          </button>
        </div>

        <label className="text-xs">
          <span className="block text-textLight mb-1">Pathogen</span>
          <select value={pathogenId} onChange={e => setPathogenId(e.target.value)} className="w-full rounded-md border border-border bg-bg px-sm py-xs text-sm">
            <option value="">Any</option>
            {(pathData?.pathogens ?? []).map(p => (<option key={p.id} value={p.id}>{p.name}</option>))}
          </select>
        </label>
        <label className="text-xs">
          <span className="block text-textLight mb-1">Metric</span>
          <select value={metric} onChange={e => setMetric(e.target.value as Metric)} className="w-full rounded-md border border-border bg-bg px-sm py-xs text-sm">
            <option value="concentration">Concentration</option>
            <option value="trend_pct">Trend %</option>
          </select>
        </label>
        <div className="grid grid-cols-[auto_1fr] gap-sm">
          <label className="text-xs">
            <span className="block text-textLight mb-1">Op</span>
            <select value={comparator} onChange={e => setComparator(e.target.value as Comparator)} className="w-full rounded-md border border-border bg-bg px-sm py-xs text-sm">
              {COMPARATORS.map(c => (<option key={c.value} value={c.value}>{c.label}</option>))}
            </select>
          </label>
          <label className="text-xs">
            <span className="block text-textLight mb-1">Threshold {metric === "trend_pct" ? "(%)" : "(copies/mL)"}</span>
            <input type="number" value={threshold} onChange={e => setThreshold(e.target.value)} className="w-full rounded-md border border-border bg-bg px-sm py-xs text-sm" />
          </label>
        </div>

        <div className="flex items-center justify-end gap-sm pt-sm">
          <button type="button" onClick={onClose} className="rounded-md border border-border bg-surface px-md py-xs text-sm hover:bg-bg cursor-pointer">Cancel</button>
          <button type="submit" disabled={create.isPending} className="inline-flex items-center gap-xs rounded-md bg-brand text-white px-md py-xs text-sm font-medium hover:bg-brand-dark disabled:opacity-50 cursor-pointer">
            <Plus className="w-4 h-4" /> {create.isPending ? "Adding…" : "Add alert"}
          </button>
        </div>
      </form>
    </div>
  );
}

export default function Alerts() {
  const { deviceId = "" } = useParams();
  const { data, isLoading } = useAlerts(deviceId);
  const update = useUpdateAlert(deviceId);
  const remove = useDeleteAlert(deviceId);
  const toast = useToast();
  const [showModal, setShowModal] = useState(false);

  if (isLoading) return <Loading />;
  const rows = data?.rules ?? [];

  return (
    <div className="flex flex-col gap-md">
      <div className="flex items-end justify-between gap-md">
        <div>
          <h1 className="text-2xl font-semibold text-text leading-tight">Alerts</h1>
          <p className="text-sm text-textLight">
            Rules that fire when an incoming result crosses a threshold.
            Triggered alerts appear in the notifications bell.
          </p>
        </div>
        <button type="button" onClick={() => setShowModal(true)} className="inline-flex items-center gap-xs rounded-md bg-brand text-white px-md py-xs text-sm font-medium hover:bg-brand-dark cursor-pointer">
          <Plus className="w-4 h-4" /> New alert
        </button>
      </div>

      {showModal && (<NewAlertModal deviceId={deviceId} onClose={() => setShowModal(false)} />)}

      <Tile className="!p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-textLight text-[11px] uppercase tracking-wider border-b border-border bg-bg">
                <th className="px-md py-sm font-semibold">Target</th>
                <th className="px-md py-sm font-semibold">Condition</th>
                <th className="px-md py-sm font-semibold text-center">Paused</th>
                <th className="px-md py-sm font-semibold">Last triggered</th>
                <th className="px-md py-sm font-semibold w-20"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-md py-lg text-center text-textLight">No alerts yet.</td>
                </tr>
              )}
              {rows.map(r => (
                <tr key={r.id} className="hover:bg-brand/5 transition-colors">
                  <td className="px-md py-sm text-text">
                    {r.pathogen_name ? <Pathogen name={r.pathogen_name} /> : <span className="text-textLight">Any pathogen</span>}
                  </td>
                  <td className="px-md py-sm text-text">{describe(r)}</td>
                  <td className="px-md py-sm text-center">
                    <button
                      type="button"
                      onClick={() => {
                        const willPause = !r.is_paused;
                        update.mutate(
                          { ruleId: r.id, body: { is_paused: willPause } },
                          {
                            onSuccess: () => toast.push(
                              willPause ? "Alert paused — no notifications until resumed" : "Alert resumed",
                              willPause ? "warn" : "success",
                            ),
                            onError: () => toast.push("Could not update alert", "error"),
                          },
                        );
                      }}
                      aria-label={r.is_paused ? "Resume alert" : "Pause alert"}
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-bg text-textLight cursor-pointer"
                    >
                      {r.is_paused ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    </button>
                  </td>
                  <td className="px-md py-sm text-textLight tabular-nums">
                    {r.last_triggered_at ? new Date(r.last_triggered_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-md py-sm text-right">
                    <button
                      type="button"
                      onClick={() => {
                        if (confirm("Delete this alert?")) {
                          remove.mutate(r.id, { onSuccess: () => toast.push("Alert deleted", "success") });
                        }
                      }}
                      aria-label="Delete alert"
                      className="inline-flex items-center justify-center w-8 h-8 rounded-md hover:bg-danger-bg text-danger-text cursor-pointer"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Tile>
    </div>
  );
}
